import AXIOS_INSTANCE from '@/src/api/axiosInstance'
import type { GeneralResMock } from '@type/general.type'
import type { LaboratoryResMock } from '@type/laboratory.type'

/**
 * Lab / imaging catalogue (phase 1): ordering from the facility list merged with the shared catalogue,
 * each test done in our lab or at an outside lab.
 *
 * Backend contract:
 * - `GET /lab_tests/search?query=` and `GET /imagings/search?query=` return facility rows first (`id` = row id,
 *   `priced` false when admin has not priced the test) then catalogue rows (`id` = `code`, e.g. `lab:fbc`).
 * - `POST/PATCH /lab_orders` take `tests: [{ lab_test_id | catalog_code | name, performed_at }]`
 *   (imaging: `imaging_id`). Free text is still a plain `name` line.
 */

export type TestKind = 'lab' | 'imaging'
export type PerformedAt = 'our_lab' | 'outside'
export type TestSource = 'facility' | 'catalogue' | 'free_text'

export const PERFORMED_AT_DEFAULT: PerformedAt = 'our_lab'

/** One row of the `/search` endpoints. */
export type CatalogueSearchRow = {
  id: number | string
  source: 'facility' | 'catalogue'
  code: string | null
  name: string
  category: string | null
  specimen: string | null
  price: number | null
  priced: boolean
}

/** A picked test in the order forms: facility row (numeric id), catalogue entry (code) or free text (typed label). */
export type TestLineOption = {
  label: string
  value: number | string
  source?: TestSource
  priced?: boolean
  performed_at?: PerformedAt
}

/** One line of the `tests` attribute sent to the backend. */
export type TestLinePayload = {
  lab_test_id?: number
  imaging_id?: number
  catalog_code?: string
  name?: string
  performed_at: PerformedAt
}

export const isCatalogueCode = (value: unknown): value is string =>
  typeof value === 'string' && /^(lab|img):/.test(value)

export const asPerformedAt = (value: unknown): PerformedAt => (value === 'outside' ? 'outside' : 'our_lab')

export function optionSource(option: TestLineOption): TestSource {
  if (option.source) return option.source
  if (typeof option.value === 'number') return 'facility'
  return isCatalogueCode(option.value) ? 'catalogue' : 'free_text'
}

export function mapSearchRow(row: CatalogueSearchRow): TestLineOption {
  if (row.source === 'catalogue') {
    return {
      label: row.name,
      performed_at: PERFORMED_AT_DEFAULT,
      priced: false,
      source: 'catalogue',
      value: String(row.code ?? row.id)
    }
  }
  return {
    label: row.name,
    performed_at: PERFORMED_AT_DEFAULT,
    priced: row.priced,
    source: 'facility',
    value: Number(row.id)
  }
}

/** Small tag next to an option: a catalogue entry we do not hold yet, or a facility test admin has not priced. */
export function optionTag(option: TestLineOption): 'Catalogue' | 'Not priced here' | null {
  const source = optionSource(option)
  if (source === 'catalogue') return 'Catalogue'
  if (source === 'facility' && option.priced === false) return 'Not priced here'
  return null
}

/** Option for a name typed into the creatable select. */
export function newFreeTextOption(inputValue: string, optionLabel?: unknown): TestLineOption {
  const label = String(optionLabel ?? inputValue)
  return { label, performed_at: PERFORMED_AT_DEFAULT, source: 'free_text', value: String(inputValue).trim() }
}

const hasPrice = (raw: unknown) => raw != null && String(raw).trim() !== ''

function facilityRowFromList(kind: TestKind, row: LaboratoryResMock): CatalogueSearchRow {
  const name = kind === 'lab' ? row.attributes.name : row.attributes.study_type || row.attributes.name
  return {
    category: row.attributes.category ?? null,
    code: null,
    id: row.id,
    name,
    price: hasPrice(row.attributes.price) ? Number(row.attributes.price) : null,
    priced: hasPrice(row.attributes.price),
    source: 'facility',
    specimen: null
  }
}

/**
 * Picker options for a query. A blank query lists the facility's own tests (the search endpoint needs a term);
 * anything else goes through `/search`, which also matches catalogue aliases ("cbc" finds FBC).
 */
export async function searchTests(kind: TestKind, inputValue: string): Promise<TestLineOption[]> {
  const query = inputValue.trim()
  const base = kind === 'lab' ? '/lab_tests' : '/imagings'

  if (!query) {
    const q = new URLSearchParams({
      'page[number]': '1',
      'page[size]': '50',
      sort_by: kind === 'lab' ? 'name' : 'study_type',
      sort_dir: 'asc'
    })
    const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock[]>>(`${base}?${q.toString()}`)
    const rows = Array.isArray(data?.data) ? data.data : []
    return rows.map(row => mapSearchRow(facilityRowFromList(kind, row)))
  }

  const { data } = await AXIOS_INSTANCE.get<{ data: CatalogueSearchRow[] }>(`${base}/search`, {
    params: { query }
  })
  const rows = Array.isArray(data?.data) ? data.data : []
  return rows.map(mapSearchRow)
}

export function toTestLinePayload(option: TestLineOption, kind: TestKind): TestLinePayload {
  const performed_at = asPerformedAt(option.performed_at)
  switch (optionSource(option)) {
    case 'facility':
      return kind === 'lab'
        ? { lab_test_id: Number(option.value), performed_at }
        : { imaging_id: Number(option.value), performed_at }
    case 'catalogue':
      return { catalog_code: String(option.value), performed_at }
    default:
      return { name: String(option.label).trim(), performed_at }
  }
}

/**
 * The `tests` attribute. Facility and catalogue lines go first, free text last, so that the order's
 * `test_names[i]` keeps lining up with its child `orders[i]` (free text creates no child order).
 */
export function buildTestsAttribute(options: TestLineOption[], kind: TestKind): TestLinePayload[] {
  const lines = options.map(option => toTestLinePayload(option, kind)).filter(line => !('name' in line) || line.name)
  return [...lines.filter(line => line.name === undefined), ...lines.filter(line => line.name !== undefined)]
}

/** Replace the `performed_at` of the option whose value matches. */
export function withPerformedAt(
  options: TestLineOption[],
  value: TestLineOption['value'],
  performed_at: PerformedAt
): TestLineOption[] {
  return options.map(option => (String(option.value) === String(value) ? { ...option, performed_at } : option))
}

type Relationships = {
  patient: { data: { id: string; type: 'patient' } }
  encounter?: { data: { id: string; type: 'encounter' } }
}

function orderRelationships(patientId: string, encounterId?: string): Relationships {
  const relationships: Relationships = { patient: { data: { id: patientId, type: 'patient' } } }
  // Walk-in (no encounter): omit the relationship — the backend creates a draft invoice.
  if (encounterId) relationships.encounter = { data: { id: encounterId, type: 'encounter' } }
  return relationships
}

/** `POST /lab_orders` body. */
export function buildLabOrderPayload(patientId: string, tests: TestLineOption[], note: string, encounterId?: string) {
  return {
    data: {
      attributes: { note: note.trim(), tests: buildTestsAttribute(tests, 'lab') },
      relationships: orderRelationships(patientId, encounterId),
      type: 'lab_order' as const
    }
  }
}

/** `POST /imaging_orders` body. */
export function buildImagingOrderPayload(
  patientId: string,
  studies: TestLineOption[],
  note: string,
  encounterId?: string
) {
  return {
    data: {
      attributes: { note: note.trim(), tests: buildTestsAttribute(studies, 'imaging') },
      relationships: orderRelationships(patientId, encounterId),
      type: 'imaging_order' as const
    }
  }
}

/** Child `order` row as sideloaded on a lab/imaging order (only the fields line resolution needs). */
export type IncludedOrderLike = {
  id: string
  type: string
  attributes?: { orderable_id?: number | string | null; performed_at?: string | null; name?: string | null }
  meta?: { status?: string }
}

export type OrderTestLine = {
  key: string
  label: string
  /** Child `order` id; empty for free text. */
  orderId: string
  lineStatus: string
  performedAt: PerformedAt
  /** `lab_test` / `imaging` row id behind the child order, when known. */
  orderableId?: number
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/**
 * One line per test of a lab/imaging order bundle.
 *
 * Child orders (`relationships.orders`) are the facility/catalogue tests; their `performed_at` says where the
 * test is done. Labels come from `resolveLabel` (a sideloaded `lab_test`/`imaging` row) or, failing that, from
 * `test_names` in order. Whatever names remain are free text, outside when listed in `outside_names`.
 */
export function buildOrderTestLines(params: {
  orderRefs: { id: string }[]
  testNames: string[]
  outsideNames?: string[]
  status: string
  included?: IncludedOrderLike[]
  resolveLabel?: (child: IncludedOrderLike | undefined) => string | undefined
}): OrderTestLine[] {
  const { orderRefs, testNames, outsideNames = [], status, included = [], resolveLabel } = params
  const remaining = testNames.map(name => (name ?? '').trim()).filter(Boolean)

  const orderLines = orderRefs.map((ref, i) => {
    const child = included.find(x => x.type === 'order' && x.id === ref.id)
    const resolved = resolveLabel?.(child)?.trim() || child?.attributes?.name?.trim()
    let label = resolved || ''
    if (label) {
      const idx = remaining.findIndex(name => sameName(name, label))
      if (idx >= 0) remaining.splice(idx, 1)
    } else {
      label = remaining.shift() || `Test ${i + 1}`
    }
    const orderableId = child?.attributes?.orderable_id != null ? Number(child.attributes.orderable_id) : NaN
    return {
      key: ref.id,
      label,
      lineStatus: String(child?.meta?.status ?? status ?? 'pending'),
      orderId: ref.id,
      performedAt: asPerformedAt(child?.attributes?.performed_at),
      ...(Number.isFinite(orderableId) ? { orderableId } : {})
    }
  })

  const freeTextLines = remaining.map((label, i) => ({
    key: `test-name-${i}-${label}`,
    label,
    lineStatus: String(status ?? 'pending'),
    orderId: '',
    performedAt: outsideNames.some(name => sameName(name, label)) ? ('outside' as const) : ('our_lab' as const)
  }))

  return [...orderLines, ...freeTextLines]
}

/** Edit-form options for an existing order's lines (keeps each line's `performed_at`). */
export function optionsFromOrderLines(lines: OrderTestLine[]): TestLineOption[] {
  return lines.map(line =>
    line.orderId && line.orderableId != null
      ? { label: line.label, performed_at: line.performedAt, source: 'facility', value: line.orderableId }
      : { label: line.label, performed_at: line.performedAt, source: 'free_text', value: line.label }
  )
}
